import { useEffect, useState } from "react";

import type { SceneBrandkit } from "./types";

export function useLoadedImage(url: string | null): HTMLImageElement | null {
  const [image, setImage] = useState<HTMLImageElement | null>(null);

  useEffect(() => {
    if (!url) {
      setImage(null);
      return;
    }

    const img = new window.Image();
    const onLoad = () => setImage(img);
    const onError = () => setImage(null);
    img.addEventListener("load", onLoad);
    img.addEventListener("error", onError);
    img.src = url;

    return () => {
      img.removeEventListener("load", onLoad);
      img.removeEventListener("error", onError);
    };
  }, [url]);

  return image;
}

export function useSceneBrandkitFonts(brandkit: SceneBrandkit | null): void {
  useEffect(() => {
    if (!brandkit || typeof document === "undefined") {
      return;
    }

    let cancelled = false;

    const load = async () => {
      for (const font of brandkit.fonts) {
        if (cancelled) {
          return;
        }

        if (font.source === "brand_confirmed") {
          const face = new FontFace(font.family, `url(${font.loadUrl})`);
          try {
            const loaded = await face.load();
            document.fonts.add(loaded);
          } catch {
            // Best-effort for admin preview.
          }
          continue;
        }

        if (!document.querySelector(`link[data-font-url="${font.loadUrl}"]`)) {
          const link = document.createElement("link");
          link.rel = "stylesheet";
          link.href = font.loadUrl;
          link.dataset.fontUrl = font.loadUrl;
          document.head.append(link);
        }

        try {
          await document.fonts.load(`16px ${font.family}`);
        } catch {
          // Best-effort for admin preview.
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [brandkit]);
}

export function useLoadingPulse(enabled = true): { pulseOpacity: number; dots: string } {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setTick(0);
      return;
    }

    const timer = window.setInterval(() => {
      setTick((value) => value + 1);
    }, 160);

    return () => {
      window.clearInterval(timer);
    };
  }, [enabled]);

  const pulseOpacity = 0.45 + 0.35 * ((Math.sin(tick / 3) + 1) / 2);
  const dotCount = (tick % 3) + 1;

  return {
    pulseOpacity,
    dots: ".".repeat(dotCount),
  };
}
